import { easternISO } from '@/lib/date';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { type TemplateControl } from '@/api/hooks';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const STATIC_OPTIONS: Record<string, string[]> = {
  'static:USStates': US_STATES,
  'static:ScanPDF': ['Separate', 'One'],
  'static:SavePDF': ['Document', 'Engagement'],
  // CAD Cleanup SS5: "Mini" removed from the Prepare 1040 dropdown.
  'static:Prepare1040': ['Yes', 'No'],
};

// Web-only display-label overrides keyed by ControlKey, so we can tweak how a
// shared template control reads in the web app without a DB change (which would
// also touch the frozen Power App). CAD Cleanup SS5: add a space -> "Prepare 1040".
const LABEL_OVERRIDES: Record<string, string> = {
  Prepare1040ExcelTax: 'Prepare 1040 Excel Tax',
};
// CAD Cleanup SS1: standardize the term to one word ("Taxpayer") everywhere a
// control label is shown, regardless of how it's spelled in the shared DB config.
const labelOf = (c: TemplateControl) =>
  (LABEL_OVERRIDES[c.ControlKey] ?? c.DisplayLabel).replace(/Tax Payer/g, 'Taxpayer');

interface Props {
  controls: TemplateControl[];
  deliverables: Record<string, boolean>;
  setDeliverable: (key: string, val: boolean) => void;
  otherDetails: Record<string, string>;
  setOtherDetail: (key: string, val: string) => void;
  propertyTaxCountries: string;
  setPropertyTaxCountries: (v: string) => void;
  /** Client whose NAICS filters the Business Activity Code list (Power Apps varFilteredBAC). */
  clientId?: string;
  disabled?: boolean;
  /** Change/Edit screen: hide the Deliverables section entirely and treat the type-specific
   *  "Other Details" fields as optional (no "*"). Per the CAD Cleanup spec — change forms
   *  shouldn't re-collect setup info; any specifics live in Other Details. */
  changeForm?: boolean;
  /** New Engagement, per-template (CAD Cleanup B1): hide the Deliverables section for
   *  templates whose tab asks for it (1040/1041). The default-checked deliverable is still
   *  seeded + submitted by the caller, so the primary Maconomy form flag is preserved.
   *  Unlike `changeForm`, Other Details stay required here. */
  hideDeliverables?: boolean;
  /** New Engagement: after a Submit attempt, highlight required fields left empty. */
  showErrors?: boolean;
  deliverablesError?: string;
  countiesError?: string;
  /** Optional override for whether an Other-Details control is required, so callers can
   *  apply conditional rules (CAD Cleanup C1: 1040 spouse fields required only when a
   *  spouse is entered). When omitted, the control's own IsRequired flag is used. */
  requiredFor?: (c: TemplateControl) => boolean;
  /** Control keys to render as a free-text input regardless of their configured InputKind.
   *  CAD Cleanup C4: on Foreign Corporation Tax, "State Incorporated" becomes a text box so
   *  users can enter a country of incorporation (the shared control is a US-states dropdown). */
  textKeys?: Set<string>;
  /** Control keys to hide entirely for this template (web-only, so the shared config and the
   *  Power App are untouched). CAD Cleanup: Fiduciary Name/Title don't apply to gift returns. */
  hideKeys?: Set<string>;
}

/** Renders the deliverables (checkboxes) + other-detail fields from the DB rules. */
export function TemplateControls({
  controls, deliverables, setDeliverable, otherDetails, setOtherDetail,
  propertyTaxCountries, setPropertyTaxCountries, clientId, disabled = false,
  changeForm = false, hideDeliverables = false, requiredFor, textKeys, hideKeys,
  showErrors = false, deliverablesError, countiesError,
}: Props) {
  // Peer Review + Business Activity Code option sources (fetched only when relevant)
  const needsPR = controls.some((c) => c.LookupSource?.includes('PeerReview'));
  const needsBAC = controls.some((c) => c.ControlKey === 'BusinessActivityCode');
  const prLevel = useLookupRows('peer-review-level', needsPR);
  const prIndustry = useLookupRows('peer-review-industry', needsPR);
  const prMustSelect = useLookupRows('peer-review-must-select', needsPR);
  const bac = useQuery({
    queryKey: ['bac', clientId ?? ''],
    queryFn: () => api.get<Record<string, unknown>[]>(`/lookups/business-activity-codes${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''}`),
    enabled: needsBAC,
    staleTime: 1000 * 60 * 10,
  });

  const deliverablesList = controls.filter((c) => c.ControlType === 'Deliverable' && !hideKeys?.has(c.ControlKey));
  const others = controls.filter((c) => c.ControlType === 'OtherDetail' && c.ControlKey !== 'PropertyTaxCountries' && !hideKeys?.has(c.ControlKey));
  const groups = [...new Set(others.map((c) => c.GroupName))];
  // Property Tax counties: rendered as its own always-shown field (see below), not as a
  // grouped "other detail", so it keeps its dedicated storage path and shows even when the
  // Deliverables box is hidden.
  const countiesCtl = controls.find((c) => c.ControlKey === 'PropertyTaxCountries');

  // DOB / Deceased dates can't be in the future — cap at yesterday to match the
  // Power Apps date pickers (EndDate = Today()-1).
  const NO_FUTURE_DATES = new Set([
    'DateOfBirthTaxPayer', 'DateDeceasedTaxPayer', 'DateOfBirthSpouse', 'DateDeceasedSpouse',
  ]);
  // Date of Death can be as recent as today (but not future) — cap at today.
  const MAX_TODAY_DATES = new Set(['DateOfDeath']);
  const isoOffset = (days: number) => easternISO(days);   // Eastern date, whatever the viewer's time zone
  const yesterdayISO = isoOffset(-1);
  const todayISO = isoOffset(0);
  const maxDateFor = (key: string) =>
    NO_FUTURE_DATES.has(key) ? yesterdayISO : MAX_TODAY_DATES.has(key) ? todayISO : undefined;

  function optionsFor(c: TemplateControl): { value: string; label: string }[] {
    if (c.LookupSource && STATIC_OPTIONS[c.LookupSource]) {
      return STATIC_OPTIONS[c.LookupSource].map((s) => ({ value: s, label: s }));
    }
    // Peer Review: save the Description (unchanged); label with the Maconomy code and text
    // (DisplayText), else the Description. The Name column is a group heading that repeats.
    const rowsToOpts = (rows?: Record<string, unknown>[]) =>
      (rows ?? []).map((r) => ({
        value: String(r.Description ?? r.Name ?? ''),
        label: String(r.DisplayText || r.Description || r.Name || ''),
      }));
    if (c.ControlKey === 'PRServiceLevel') return rowsToOpts(prLevel.data);
    if (c.ControlKey === 'PRIndustry') return rowsToOpts(prIndustry.data);
    if (c.ControlKey === 'PRMustSelect') return rowsToOpts(prMustSelect.data);
    if (c.ControlKey === 'BusinessActivityCode')
      return (bac.data ?? []).map((r) => ({ value: String(r.BusinessActivityCode ?? ''), label: String(r.BusinessActivityCode ?? '') }));
    return [];
  }

  return (
    <div className="space-y-6">
      {/* Deliverables — hidden on the change form (A2) and on New for the templates
          flagged in CAD Cleanup (B1); the default-checked deliverable is still submitted. */}
      {!changeForm && !hideDeliverables && deliverablesList.length > 0 && (
        <div className="card">
          <h3 className="mb-3 border-b border-border pb-2 text-sm font-semibold uppercase tracking-wide text-foreground">Deliverables</h3>
          {showErrors && deliverablesError && (
            <p data-field-error="true" className="mb-2 text-xs font-medium text-destructive">{deliverablesError}</p>
          )}
          <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {deliverablesList.map((c) => (
              <label key={c.ControlKey} className="flex items-start gap-2 text-sm leading-tight">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!deliverables[c.ControlKey]}
                  disabled={disabled}
                  onChange={(e) => setDeliverable(c.ControlKey, e.target.checked)}
                />
                <span>{labelOf(c)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Counties for property tax filing — its own always-shown, required field (CAD Cleanup
          AI9). Pulled out of the Deliverables box so it survives the box being hidden and is
          no longer gated by the optional "Property Tax" checkbox. */}
      {countiesCtl && (
        <div className="card">
          <h3 className="mb-3 border-b border-border pb-2 text-sm font-semibold uppercase tracking-wide text-foreground">{countiesCtl.GroupName}</h3>
          <div className={`max-w-xl ${showErrors && countiesError ? 'field-error' : ''}`} data-field-error={showErrors && countiesError ? 'true' : undefined}>
            <label className="label">{labelOf(countiesCtl)}{countiesCtl.IsRequired && !changeForm ? ' *' : ''}</label>
            <input className="input" disabled={disabled} value={propertyTaxCountries} onChange={(e) => setPropertyTaxCountries(e.target.value)} />
            {showErrors && countiesError && <p className="mt-1 text-xs font-medium text-destructive">{countiesError}</p>}
          </div>
        </div>
      )}

      {/* Other details, grouped */}
      {groups.map((g) => (
        <div key={g} className="card">
          <h3 className="mb-3 border-b border-border pb-2 text-sm font-semibold uppercase tracking-wide text-foreground">{g}</h3>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">

            {others.filter((c) => c.GroupName === g).map((c) => {
              const required = requiredFor ? requiredFor(c) : c.IsRequired;
              const asText = textKeys?.has(c.ControlKey);
              const missing = showErrors && required && !changeForm && c.IsEditable && !String(otherDetails[c.ControlKey] ?? '').trim();
              return (
              <div key={c.ControlKey} className={missing ? 'field-error' : undefined} data-field-error={missing ? 'true' : undefined}>
                <label className="label">{labelOf(c)}{required && !changeForm ? ' *' : ''}</label>
                {c.InputKind === 'date' && !asText ? (
                  // Clear button lets users remove a mis-entered date (e.g. a DOB
                  // typed into Date Deceased) without follow-up — shows only when set.
                  <div className="flex items-center gap-1.5">
                    <input type="date" className="input" disabled={disabled}
                      max={maxDateFor(c.ControlKey)}
                      value={otherDetails[c.ControlKey] ?? ''} onChange={(e) => setOtherDetail(c.ControlKey, e.target.value)} />
                    {!disabled && (otherDetails[c.ControlKey] ?? '') !== '' && (
                      <button
                        type="button"
                        title="Clear date"
                        aria-label="Clear date"
                        className="shrink-0 rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-primary"
                        onClick={() => setOtherDetail(c.ControlKey, '')}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ) : c.InputKind === 'dropdown' && !asText ? (
                  <select className="input" disabled={disabled} value={otherDetails[c.ControlKey] ?? ''} onChange={(e) => setOtherDetail(c.ControlKey, e.target.value)}>
                    <option value="">— select —</option>
                    {optionsFor(c).map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input className="input" disabled={disabled} value={otherDetails[c.ControlKey] ?? ''} onChange={(e) => setOtherDetail(c.ControlKey, e.target.value)} />
                )}
                {missing && <p className="mt-1 text-xs font-medium text-destructive">Required</p>}
              </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function useLookupRows(name: string, enabled: boolean) {
  return useQuery({
    queryKey: ['lookup', name],
    queryFn: () => api.get<Record<string, unknown>[]>(`/lookups/${name}`),
    enabled,
    staleTime: 1000 * 60 * 30,
  });
}
